import * as THREE from 'three';
import type { AppRenderer } from '../../render/Renderer';

interface RendererAudit {
  pixelRatio: number;
  canvas: {
    clientWidth: number;
    clientHeight: number;
    drawingBufferWidth: number;
    drawingBufferHeight: number;
  };
  render: {
    calls: number;
    triangles: number;
    points: number;
    lines: number;
    frame: number;
  };
  memory: {
    geometries: number;
    textures: number;
  };
  programs: number | null;
  shadowMapEnabled: boolean;
}

interface MeshAudit {
  path: string;
  name: string;
  type: string;
  triangles: number;
  drawCalls: number;
  instances: number;
  visible: boolean;
  castShadow: boolean;
  receiveShadow: boolean;
  materialCount: number;
  geometryId: number;
  frustumCulled: boolean;
}

interface MaterialAudit {
  uuid: string;
  name: string;
  type: string;
  uses: number;
  transparent: boolean;
  textureCount: number;
}

export interface SceneAuditSnapshot {
  renderer: RendererAudit;
  scene: {
    totalNodes: number;
    visibleNodes: number;
    meshes: number;
    visibleMeshes: number;
    instancedMeshes: number;
    skinnedMeshes: number;
    lods: number;
    uniqueGeometries: number;
    uniqueMaterials: number;
    uniqueTextures: number;
    castShadowMeshes: number;
    receiveShadowMeshes: number;
    frustumCulledFalseMeshes: number;
    estimatedTriangles: number;
    visibleTriangles: number;
    estimatedDrawCalls: number;
    visibleEstimatedDrawCalls: number;
    objectTypes: Record<string, number>;
  };
  topMeshesByTriangles: MeshAudit[];
  topMeshesByDrawCalls: MeshAudit[];
  topMaterialsByUse: MaterialAudit[];
}

const TEXTURE_KEYS = [
  'map',
  'aoMap',
  'alphaMap',
  'bumpMap',
  'displacementMap',
  'emissiveMap',
  'envMap',
  'lightMap',
  'metalnessMap',
  'normalMap',
  'roughnessMap',
  'specularMap',
  'transmissionMap',
  'thicknessMap',
  'clearcoatMap',
  'clearcoatNormalMap',
  'clearcoatRoughnessMap',
] as const;

export function collectSceneAudit(
  scene: THREE.Scene,
  renderer: AppRenderer,
): SceneAuditSnapshot {
  const drawingBufferSize = new THREE.Vector2();
  renderer.getDrawingBufferSize(drawingBufferSize);

  const objectTypes: Record<string, number> = {};
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  const materialUse = new Map<string, MaterialAudit>();
  const meshes: MeshAudit[] = [];

  let totalNodes = 0;
  let visibleNodes = 0;
  let meshCount = 0;
  let visibleMeshes = 0;
  let instancedMeshes = 0;
  let skinnedMeshes = 0;
  let lods = 0;
  let castShadowMeshes = 0;
  let receiveShadowMeshes = 0;
  let frustumCulledFalseMeshes = 0;
  let estimatedTriangles = 0;
  let visibleTriangles = 0;
  let estimatedDrawCalls = 0;
  let visibleEstimatedDrawCalls = 0;

  scene.traverse((object) => {
    totalNodes++;
    objectTypes[object.type] = (objectTypes[object.type] ?? 0) + 1;
    const visible = isEffectivelyVisible(object);
    if (visible) visibleNodes++;
    if ((object as { isLOD?: boolean }).isLOD) lods++;

    const mesh = object as THREE.Mesh & {
      isMesh?: boolean;
      isInstancedMesh?: boolean;
      isSkinnedMesh?: boolean;
      count?: number;
    };
    if (!mesh.isMesh) return;

    meshCount++;
    if (visible) visibleMeshes++;
    if (mesh.isInstancedMesh) instancedMeshes++;
    if (mesh.isSkinnedMesh) skinnedMeshes++;
    if (mesh.castShadow) castShadowMeshes++;
    if (mesh.receiveShadow) receiveShadowMeshes++;
    if (!mesh.frustumCulled) frustumCulledFalseMeshes++;

    const geometry = mesh.geometry;
    const instanceCount = mesh.isInstancedMesh ? Math.max(0, mesh.count ?? 0) : 1;
    const triangles = Math.round(geometryTriangleCount(geometry) * instanceCount);
    const drawCalls = estimateDrawCalls(mesh);
    const meshMaterials = normalizeMaterials(mesh.material);

    geometries.add(geometry);
    for (const material of meshMaterials) {
      materials.add(material);
      const materialTextures = collectMaterialTextures(material);
      for (const texture of materialTextures) textures.add(texture);
      const existing = materialUse.get(material.uuid);
      if (existing) {
        existing.uses++;
      } else {
        materialUse.set(material.uuid, {
          uuid: material.uuid,
          name: material.name || '<unnamed>',
          type: material.type,
          uses: 1,
          transparent: material.transparent,
          textureCount: materialTextures.length,
        });
      }
    }

    estimatedTriangles += triangles;
    estimatedDrawCalls += drawCalls;
    if (visible) {
      visibleTriangles += triangles;
      visibleEstimatedDrawCalls += drawCalls;
    }

    meshes.push({
      path: objectPath(mesh),
      name: mesh.name || '<unnamed>',
      type: mesh.type,
      triangles,
      drawCalls,
      instances: instanceCount,
      visible,
      castShadow: mesh.castShadow,
      receiveShadow: mesh.receiveShadow,
      materialCount: meshMaterials.length,
      geometryId: geometry.id,
      frustumCulled: mesh.frustumCulled,
    });
  });

  const visibleMeshAudits = meshes.filter((mesh) => mesh.visible);
  const topByTriangles = [...visibleMeshAudits]
    .sort((a, b) => b.triangles - a.triangles)
    .slice(0, 25);
  const topByDrawCalls = [...visibleMeshAudits]
    .sort((a, b) => b.drawCalls - a.drawCalls || b.triangles - a.triangles)
    .slice(0, 25);
  const topMaterials = [...materialUse.values()]
    .sort((a, b) => b.uses - a.uses)
    .slice(0, 25);

  return {
    renderer: {
      pixelRatio: renderer.getPixelRatio(),
      canvas: {
        clientWidth: renderer.domElement.clientWidth,
        clientHeight: renderer.domElement.clientHeight,
        drawingBufferWidth: drawingBufferSize.x,
        drawingBufferHeight: drawingBufferSize.y,
      },
      render: {
        calls: renderer.info.render.calls,
        triangles: renderer.info.render.triangles,
        points: renderer.info.render.points,
        lines: renderer.info.render.lines,
        frame: rendererFrame(renderer.info),
      },
      memory: {
        geometries: renderer.info.memory.geometries,
        textures: renderer.info.memory.textures,
      },
      programs: rendererProgramCount(renderer.info),
      shadowMapEnabled: renderer.shadowMap.enabled,
    },
    scene: {
      totalNodes,
      visibleNodes,
      meshes: meshCount,
      visibleMeshes,
      instancedMeshes,
      skinnedMeshes,
      lods,
      uniqueGeometries: geometries.size,
      uniqueMaterials: materials.size,
      uniqueTextures: textures.size,
      castShadowMeshes,
      receiveShadowMeshes,
      frustumCulledFalseMeshes,
      estimatedTriangles,
      visibleTriangles,
      estimatedDrawCalls,
      visibleEstimatedDrawCalls,
      objectTypes,
    },
    topMeshesByTriangles: topByTriangles,
    topMeshesByDrawCalls: topByDrawCalls,
    topMaterialsByUse: topMaterials,
  };
}

function rendererFrame(info: AppRenderer['info']): number {
  return (info as { render?: { frame?: number }; frame?: number }).render?.frame
    ?? (info as { frame?: number }).frame
    ?? 0;
}

function rendererProgramCount(info: AppRenderer['info']): number | null {
  const webglPrograms = (info as { programs?: unknown[] }).programs;
  if (Array.isArray(webglPrograms)) return webglPrograms.length;
  return (info as { memory?: { programs?: number } }).memory?.programs ?? null;
}

function geometryTriangleCount(geometry: THREE.BufferGeometry): number {
  const indexCount = geometry.index?.count;
  if (typeof indexCount === 'number') return indexCount / 3;
  const position = geometry.getAttribute('position');
  return position ? position.count / 3 : 0;
}

function estimateDrawCalls(mesh: THREE.Mesh): number {
  const materialCount = normalizeMaterials(mesh.material).length;
  if (Array.isArray(mesh.material) && mesh.geometry.groups.length > 0) {
    return mesh.geometry.groups.length;
  }
  return Math.max(1, materialCount);
}

function normalizeMaterials(material: THREE.Material | THREE.Material[]): THREE.Material[] {
  return Array.isArray(material) ? material.filter(Boolean) : [material];
}

function collectMaterialTextures(material: THREE.Material): THREE.Texture[] {
  const record = material as unknown as Record<string, unknown>;
  const out: THREE.Texture[] = [];
  for (const key of TEXTURE_KEYS) {
    const value = record[key];
    if (isTexture(value)) out.push(value);
  }
  return out;
}

function isTexture(value: unknown): value is THREE.Texture {
  return Boolean(value && typeof value === 'object' && (value as { isTexture?: boolean }).isTexture);
}

function isEffectivelyVisible(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object;
  while (current) {
    if (!current.visible) return false;
    current = current.parent;
  }
  return true;
}

function objectPath(object: THREE.Object3D): string {
  const parts: string[] = [];
  let current: THREE.Object3D | null = object;
  while (current) {
    parts.push(current.name || current.type);
    current = current.parent;
  }
  return parts.reverse().join('/');
}
